import { FC, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { Button, Label, Select, Textarea, Spinner, Alert } from "flowbite-react";
import { HiInformationCircle } from "react-icons/hi";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import {
  useGetAIAgentQuery,
  useUpdateAIAgentMutation,
  AIAgentUpdatePayload,
} from "@/api/companyApi/aiAgent";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { agentRoles } from "@/constants/agent";

const LLM_OPTIONS = [
  { value: "gpt-4", label: "GPT-4" },
  { value: "claude-3", label: "Claude" },
];

const AIAgentConfigTab: FC = () => {
  const { t } = useTranslation(["companySetting", "common"]);
  const currentUser = useCurrentUser();
  // Frontline / Backoffice / Other can VIEW the AI agent config but cannot
  // mutate it. Only management / company_admin / super_admin may save,
  // sync KB, or toggle the agent on/off.
  const canEdit =
    currentUser?.agent_role === agentRoles.management ||
    currentUser?.agent_role === agentRoles.company_admin ||
    currentUser?.agent_role === agentRoles.super_admin;
  const { data: agent, isLoading, isError, refetch } = useGetAIAgentQuery();

  const [updateAgent, { isLoading: isSaving }] = useUpdateAIAgentMutation();

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors, isDirty },
  } = useForm<AIAgentUpdatePayload>({
    defaultValues: { company_scope: "", chosen_llm: "gpt-4" },
  });

  useEffect(() => {
    if (agent) {
      reset({
        company_scope: agent.company_scope ?? "",
        chosen_llm: agent.chosen_llm ?? "gpt-4",
      });
    }
  }, [agent, reset]);

  const onSubmit = async (values: AIAgentUpdatePayload) => {
    try {
      await updateAgent(values).unwrap();
      toast.success(t("companySetting:saveSuccess"));
      refetch();
    } catch (err: any) {
      toast.error(err?.data?.error || t("companySetting:saveError"));
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  if (isError) {
    return (
      <Alert color="failure" className="mt-4">
        {t("companySetting:loadError")}
      </Alert>
    );
  }

  const isActive = agent?.is_active !== false;

  return (
    <div className="max-w-3xl mx-auto">
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
          {t("companySetting:aiAgent.title")}
        </h2>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          {t("companySetting:aiAgent.description")}
        </p>
      </div>

      {!canEdit && (
        <Alert color="info" icon={HiInformationCircle} className="mb-4">
          {t("companySetting:aiAgent.readOnlyNotice", {
            defaultValue:
              "You can view the AI Agent configuration. Only managers and company admins can change it.",
          })}
        </Alert>
      )}

      {/* AI Agent status panel */}
      <div className="mb-6 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4">
        <div className="mb-3">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
            {t("companySetting:aiAgent.status", { defaultValue: "AI Agent Status" })}
          </h3>
        </div>

        <div className="flex items-center justify-between p-3 rounded-md bg-gray-50 dark:bg-gray-900/50">
          <span className="text-sm text-gray-600 dark:text-gray-300">
            {t("companySetting:training.agentStatus")}
          </span>
          <span
            className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-medium ${
              isActive
                ? "bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300"
                : "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300"
            }`}
          >
            {t(
              isActive
                ? "companySetting:training.active"
                : "companySetting:training.inactive"
            )}
          </span>
        </div>

        {!isActive && (
          <div className="mt-3 flex items-start gap-2 rounded-md border border-gray-300 bg-gray-50 dark:border-gray-600 dark:bg-gray-900/50 px-3 py-2 text-sm text-gray-700 dark:text-gray-300">
            <HiInformationCircle className="w-5 h-5 mt-0.5 flex-shrink-0" />
            <p>{t("companySetting:training.inactiveNote")}</p>
          </div>
        )}
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div>
          <Label htmlFor="chosen_llm" value={t("companySetting:aiAgent.llmLabel")} />
          <Controller
            name="chosen_llm"
            control={control}
            rules={{ required: t("companySetting:required") as string }}
            render={({ field }) => (
              <Select
                id="chosen_llm"
                value={field.value || ""}
                onChange={(e) => field.onChange(e.target.value)}
                color={errors.chosen_llm ? "failure" : undefined}
                className="mt-1"
                disabled
              >
                {/* llm selection disabled   not supported yet */}
                {LLM_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </Select>
            )}
          />
          {errors.chosen_llm && (
            <p className="text-sm text-red-600 mt-1">{errors.chosen_llm.message}</p>
          )}
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            {t("companySetting:aiAgent.llmHelp")}
          </p>
        </div>

        <div>
          <Label htmlFor="company_scope" value={t("companySetting:aiAgent.scopeLabel")} />
          <Textarea
            id="company_scope"
            rows={8}
            placeholder={t("companySetting:aiAgent.scopePlaceholder")}
            {...register("company_scope", {
              required: t("companySetting:required") as string,
              maxLength: {
                value: 5000,
                message: t("companySetting:aiAgent.scopeMaxLength"),
              },
            })}
            color={errors.company_scope ? "failure" : undefined}
            className="mt-1"
            disabled={!canEdit}
          />
          {errors.company_scope && (
            <p className="text-sm text-red-600 mt-1">
              {errors.company_scope.message}
            </p>
          )}
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            {t("companySetting:aiAgent.scopeHelp")}
          </p>
        </div>

        {canEdit && (
          <div className="flex justify-end pt-4 border-t border-gray-200 dark:border-gray-700">
            <Button type="submit" disabled={isSaving || !isDirty} color="blue">
              {isSaving ? (
                <>
                  <Spinner size="sm" className="mr-2" />
                  {t("companySetting:saving")}
                </>
              ) : (
                t("common:save")
              )}
            </Button>
          </div>
        )}
      </form>
    </div>
  );
};

export default AIAgentConfigTab;
